import {
  Save,
  Eye,
  Undo2,
  Redo2,
  Copy,
  Trash2,
  Loader2,
  X,
  Pencil,
  Check
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CertificateTemplateType, CERTIFICATE_TEMPLATE_TYPE_LABELS } from '@/types/certificate-template.types';

interface EditorToolbarProps {
  templateName: string;
  templateType: CertificateTemplateType;
  isEditMode: boolean;
  isSaving: boolean;
  canUndo: boolean;
  canRedo: boolean;
  hasSelectedElement: boolean;
  onBack: () => void;
  onSave: () => void;
  onPreview: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onClearContent?: () => void;
  onRestoreContent?: () => void;
  /** Rename the template; reject with an Error whose message is shown under the field */
  onRename?: (name: string) => Promise<void>;
}

export default function EditorToolbar({
  templateName,
  templateType,
  isEditMode,
  isSaving,
  canUndo,
  canRedo,
  hasSelectedElement,
  onBack,
  onSave,
  onPreview,
  onUndo,
  onRedo,
  onDuplicate,
  onDelete,
  onClearContent,
  onRestoreContent,
  onRename,
}: EditorToolbarProps) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [isRenameSaving, setIsRenameSaving] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming) nameInputRef.current?.select();
  }, [isRenaming]);
  // The field is disabled while saving and loses focus; give it back after an error
  useEffect(() => {
    if (isRenaming && !isRenameSaving) nameInputRef.current?.focus();
  }, [isRenaming, isRenameSaving]);

  const startRename = () => {
    setDraftName(templateName);
    setRenameError('');
    setIsRenaming(true);
  };
  const cancelRename = () => {
    if (!isRenameSaving) setIsRenaming(false);
  };
  const confirmRename = async () => {
    const name = draftName.trim();
    if (name === templateName.trim()) { setIsRenaming(false); return; }
    if (name.length < 3) { setRenameError('Name must be at least 3 characters.'); return; }
    setIsRenameSaving(true);
    try {
      await onRename!(name);
      setIsRenaming(false);
    } catch (err) {
      setRenameError((err as Error)?.message || 'Could not rename the template.');
    } finally {
      setIsRenameSaving(false);
    }
  };

  return (
    <div className="flex-shrink-0 bg-white border-b shadow-sm">
      {/* Main Toolbar */}
      <div className="flex items-center justify-between px-4 py-2">
        {/* Left section - Exit button and template info */}
        <div className="flex items-center gap-3">
          {/* Exit Button */}
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            title="Exit to templates list"
          >
            <X className="w-4 h-4" />
            Exit
          </button>

          <div className="border-l pl-4">
            {isRenaming ? (
              // Escape cancels from the field or its buttons
              <div onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); cancelRename(); } }}>
                <div className="flex items-center gap-1">
                  <input
                    ref={nameInputRef}
                    value={draftName}
                    onChange={(e) => { setDraftName(e.target.value); setRenameError(''); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); confirmRename(); }
                    }}
                    maxLength={100}
                    disabled={isRenameSaving}
                    aria-label="Template name"
                    aria-invalid={!!renameError}
                    className={`w-64 px-2 py-1 text-base font-semibold text-gray-900 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${renameError ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  <button
                    onClick={confirmRename}
                    disabled={isRenameSaving}
                    className="p-1.5 text-green-700 hover:bg-green-50 rounded-md disabled:opacity-50"
                    title="Save name"
                    aria-label="Save name"
                  >
                    {isRenameSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={cancelRename}
                    disabled={isRenameSaving}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-50"
                    title="Cancel"
                    aria-label="Cancel rename"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {renameError && <p className="text-xs text-red-600 mt-0.5" role="alert">{renameError}</p>}
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <h1 className="text-lg font-semibold text-gray-900">
                  {templateName || 'Untitled Template'}
                </h1>
                {onRename && (
                  <button
                    onClick={startRename}
                    disabled={isSaving}
                    className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                    title="Rename template"
                    aria-label="Rename template"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-0.5">
              {CERTIFICATE_TEMPLATE_TYPE_LABELS[templateType] || templateType}
            </p>
          </div>
        </div>

        {/* Center section - Tools */}
        <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
          {/* Undo/Redo */}
          <div className="flex items-center gap-0.5 pr-2 border-r">
            <button
              onClick={onUndo}
              disabled={!canUndo}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              <Undo2 className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={onRedo}
              disabled={!canRedo}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              <Redo2 className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Element actions */}
          <div className="flex items-center gap-0.5 px-2 border-r">
            <button
              onClick={onDuplicate}
              disabled={!hasSelectedElement}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Duplicate (Ctrl+D)"
            >
              <Copy className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={onDelete}
              disabled={!hasSelectedElement}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Delete (Del)"
            >
              <Trash2 className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Clear / Restore content */}
          <div className="flex items-center gap-1 pl-2">
            {onClearContent && (
              <button
                onClick={onClearContent}
                className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                title="Remove all elements and background"
              >
                Clear All
              </button>
            )}
            {onRestoreContent && (
              <button
                onClick={onRestoreContent}
                className="px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                title="Restore cleared template"
              >
                Restore
              </button>
            )}
          </div>
        </div>

        {/* Right section - Actions */}
        <div className="flex items-center gap-2">
          {/* Preview — temporarily hidden, will revisit later
          <button
            onClick={onPreview}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          */}

          {/* Save */}
          <button
            onClick={onSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? 'Update' : 'Save'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
